import {
  getActionDropDownName,
  isActionDropDownMain,
} from '@/common/utils/elementLayoutActions'
import {
  RegisterActionKey,
  UnRegisterActionKey,
} from '@/components/platform/injection-keys'
import type { ActionHooks } from '@/components/platform/types'
import {
  resolveActionLayoutLevelText,
  resolveActionLayoutTextId,
} from '@/services/action/actionScriptRuntime'
import {
  computed,
  inject,
  onUnmounted,
  reactive,
  watch,
  type ComputedRef,
} from 'vue'

type GroupedActionItem = {
  type: string
  actions?: Record<string, unknown>[]
}

function parseActionId(action: Record<string, unknown>): number | null {
  const raw = (action._attributes as { id?: string | number } | undefined)?.id
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function buildDropdownActionCtx(
  action: Record<string, unknown>,
  formId: number,
  elementId?: string,
  panelId?: string,
) {
  const layout = (action.layout as Record<string, unknown> | undefined) ?? {}
  const layoutLevel = resolveActionLayoutLevelText(layout)
  const layoutId = resolveActionLayoutTextId(layout)
  const id = parseActionId(action) ?? 0
  return {
    id,
    name: String((action.name as { _text?: unknown } | undefined)?._text ?? '').trim(),
    formId,
    panelId: panelId ?? '',
    elementId: elementId ?? (layoutLevel === 'element' && layoutId ? layoutId : undefined),
    pageId: layoutLevel === 'page' && layoutId ? layoutId : undefined,
    level: layoutLevel,
    data: action,
    selectedData: null,
  }
}

/**
 * Регистрирует пункты dropdown-группы в getActions(), чтобы ACTION-states (setVisible/setEnable)
 * применялись так же, как для MAction на главной кнопке группы.
 */
export function useDropdownGroupActionRegistry(options: {
  groupedActions: ComputedRef<GroupedActionItem[]>
  formId: ComputedRef<number>
  elementId?: ComputedRef<string | undefined>
  panelId?: string
  onActivateDropdown: (action: Record<string, unknown>) => void | Promise<void>
}) {
  const registerAction = inject(RegisterActionKey, () => {})
  const unRegisterAction = inject(UnRegisterActionKey, () => {})
  const dropdownVisible = reactive<Record<number, boolean>>({})
  const dropdownEnabled = reactive<Record<number, boolean>>({})
  const dropdownTitles = reactive<Record<number, string>>({})
  const registeredIds = new Set<number>()

  const isDropdownMenuActionVisible = (action: Record<string, unknown>) => {
    const id = parseActionId(action)
    if (id == null) return true
    return dropdownVisible[id] !== false
  }

  const isDropdownMenuActionEnabled = (action: Record<string, unknown>) => {
    const id = parseActionId(action)
    if (id == null) return true
    return dropdownEnabled[id] !== false
  }

  const dropdownActionLabel = (action: Record<string, unknown>) => {
    const id = parseActionId(action)
    if (id != null && Object.prototype.hasOwnProperty.call(dropdownTitles, id)) {
      return dropdownTitles[id]
    }
    const title = action.title as { _text?: unknown } | string | undefined
    const name = action.name as { _text?: unknown } | string | undefined
    const titleText = typeof title === 'string' ? title : title?._text
    const nameText = typeof name === 'string' ? name : name?._text
    return String(titleText || nameText || 'Action')
  }

  const syncRegistry = () => {
    const nextIds = new Set<number>()
    const formId = options.formId.value
    const elementId = options.elementId?.value

    for (const item of options.groupedActions.value) {
      if (item.type !== 'group' || !Array.isArray(item.actions)) continue
      for (const action of item.actions) {
        const dropDownName = getActionDropDownName(action)
        if (!dropDownName || isActionDropDownMain(action)) continue
        const id = parseActionId(action)
        if (id == null) continue
        nextIds.add(id)
        const applyTitle = (value: string) => {
          dropdownTitles[id] = value == null ? '' : String(value)
        }
        if (registeredIds.has(id)) continue

        const ctx = buildDropdownActionCtx(action, formId, elementId, options.panelId)
        const hooks: ActionHooks = {
          onActivate: () => {
            void options.onActivateDropdown(action)
          },
          getAction: () => ({
            doAction: () => {
              void options.onActivateDropdown(action)
            },
          }),
          setVisible: (value: boolean) => {
            dropdownVisible[id] = value
          },
          setEnable: (value: boolean) => {
            dropdownEnabled[id] = value
          },
          setEnabled: (value: boolean) => {
            dropdownEnabled[id] = value
          },
          setTitle: applyTitle,
          setToolTipText: () => {},
          setIcon: () => {},
          getVisualContainer: () => ({
            setVisible: (value: boolean) => {
              dropdownVisible[id] = value
            },
            setEnable: (value: boolean) => {
              dropdownEnabled[id] = value
            },
            setEnabled: (value: boolean) => {
              dropdownEnabled[id] = value
            },
            setTitle: applyTitle,
            setToolTipText: () => {},
            setIcon: () => {},
          }),
        }
        registerAction(ctx, hooks)
        registeredIds.add(id)
      }
    }

    for (const id of [...registeredIds]) {
      if (!nextIds.has(id)) {
        unRegisterAction({ id })
        registeredIds.delete(id)
        delete dropdownVisible[id]
        delete dropdownEnabled[id]
        delete dropdownTitles[id]
      }
    }
  }

  watch(options.groupedActions, syncRegistry, { immediate: true, flush: 'post' })

  onUnmounted(() => {
    for (const id of registeredIds) {
      unRegisterAction({ id })
    }
    registeredIds.clear()
  })

  return {
    isDropdownMenuActionVisible,
    isDropdownMenuActionEnabled,
    dropdownActionLabel,
  }
}
